import type { ReactNode } from "react"

type HighlightRange = {
  start: number
  end: number
}

const uniqueNonEmptyKeywords = (matchContents: string[]): string[] => {
  const seen = new Set<string>()
  for (const k of matchContents) {
    if (k.length > 0) seen.add(k)
  }
  return [...seen]
}

const collectRangesForAllKeywords = (content: string, keywords: string[]): HighlightRange[] => {
  const ranges: HighlightRange[] = []
  for (const keyword of keywords) {
    let searchFrom = 0
    let index = content.indexOf(keyword, searchFrom)
    while (index !== -1) {
      ranges.push({ start: index, end: index + keyword.length })
      searchFrom = index + 1
      index = content.indexOf(keyword, searchFrom)
    }
  }
  return ranges
}

const mergeOverlappingRanges = (ranges: HighlightRange[]): HighlightRange[] => {
  const sorted = [...ranges].sort((a, b) => a.start - b.start || b.end - a.end)
  const merged: HighlightRange[] = []

  for (const range of sorted) {
    const last = merged[merged.length - 1]
    const isSeparate = last == null || last.end < range.start

    if (isSeparate) {
      merged.push({ ...range })
    } else {
      last.end = Math.max(last.end, range.end)
    }
  }

  return merged
}

const rangesToFragmentNodes = (content: string, merged: HighlightRange[]): ReactNode[] => {
  const nodes: ReactNode[] = []
  let segmentStart = 0

  merged.forEach((range, i) => {
    if (range.start > segmentStart) {
      nodes.push(content.slice(segmentStart, range.start))
    }
    nodes.push(
      <span key={`hl-${range.start}-${range.end}-${i}`} className="rounded-sm bg-primary-100">
        {content.slice(range.start, range.end)}
      </span>
    )
    segmentStart = range.end
  })

  if (segmentStart < content.length) {
    nodes.push(content.slice(segmentStart))
  }

  return nodes
}

/** `content` 안에서 `matchContents` 키워드와 일치하는 구간에 배경 하이라이트 (리뷰 상세 전용) */
export const renderContentWithMatchHighlights = (
  content: string,
  matchContents?: string[] | null
): ReactNode => {
  if (matchContents == null || matchContents.length === 0) {
    return content
  }

  const keywords = uniqueNonEmptyKeywords(matchContents)
  if (keywords.length === 0) {
    return content
  }

  const rawRanges = collectRangesForAllKeywords(content, keywords)
  if (rawRanges.length === 0) {
    return content
  }

  const merged = mergeOverlappingRanges(rawRanges)
  return <>{rangesToFragmentNodes(content, merged)}</>
}
